import { Sparkles, Search, Quote, BarChart3, FileText, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import useTranslation from '@/hooks/useTranslation';
import type { SuggestedQuestion } from '@/reactQuery/researchAssistant';

interface SuggestedQuestionsProps {
  suggestions: SuggestedQuestion[];
  interviewGoal?: string;
  respondentCount?: number;
  onSelectQuestion: (question: string) => void;
  /** Disable click handlers while a request is pending. */
  disabled?: boolean;
}

/**
 * Per-category brand accents (icon + color theme only). Labels live in
 * the translation JSON and are resolved at render time so the category
 * headers honour the dashboard language (en / nl).
 *
 * Colors are pulled from the Tellet palette: emerald (primary),
 * amber (highlights), indigo (analytical), pink (creative/secondary).
 */
interface CategoryStyle {
  icon: typeof Search;
  /** Solid icon background for the category badge */
  iconBg: string;
  /** Icon foreground color */
  iconColor: string;
  /** Card border color (default state) */
  border: string;
  /** Card hover border + background */
  hoverBorder: string;
  hoverBg: string;
  /** Category label text color */
  labelColor: string;
}

// Per design strategy: all question categories use uniform primary
// green — icon shape provides distinction, not color (they're peers).
const CATEGORY_STYLES: Record<SuggestedQuestion['category'], CategoryStyle> = {
  explore: {
    icon: BarChart3,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    border: 'border-neutral-200',
    hoverBorder: 'hover:border-primary/30',
    hoverBg: 'hover:bg-primary/5',
    labelColor: 'text-primary',
  },
  quotes: {
    icon: Quote,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    border: 'border-neutral-200',
    hoverBorder: 'hover:border-primary/30',
    hoverBg: 'hover:bg-primary/5',
    labelColor: 'text-primary',
  },
  compare: {
    icon: Search,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    border: 'border-neutral-200',
    hoverBorder: 'hover:border-primary/30',
    hoverBg: 'hover:bg-primary/5',
    labelColor: 'text-primary',
  },
  report: {
    icon: FileText,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    border: 'border-neutral-200',
    hoverBorder: 'hover:border-primary/30',
    hoverBg: 'hover:bg-primary/5',
    labelColor: 'text-primary',
  },
};

const CATEGORY_LABEL_KEYS: Record<SuggestedQuestion['category'], string> = {
  explore: 'msg.suggestCategoryExplore',
  quotes: 'msg.suggestCategoryQuotes',
  compare: 'msg.suggestCategoryCompare',
  report: 'msg.suggestCategoryReport',
};

/** Truncate long interview goals so the description doesn't dominate the layout. */
function truncate(s: string, max: number): string {
  if (!s) return '';
  return s.length > max ? `${s.slice(0, max - 1).trim()}…` : s;
}

function SuggestedQuestions({
  suggestions,
  interviewGoal,
  respondentCount,
  onSelectQuestion,
  disabled,
}: SuggestedQuestionsProps) {
  const { lang } = useTranslation();

  // Fallback suggestions are resolved at render time so they honour the
  // dashboard language (en / nl). Suggestions coming from the backend
  // already use the project's analysis_language — this fallback only
  // fires when the backend returned an empty array.
  const fallbackSuggestions: SuggestedQuestion[] = [
    { category: 'explore', text: lang.get('msg.fallbackSuggestionThemes') },
    { category: 'quotes', text: lang.get('msg.fallbackSuggestionQuotes') },
    { category: 'report', text: lang.get('msg.fallbackSuggestionReport') },
  ];
  const displaySuggestions = suggestions.length > 0 ? suggestions : fallbackSuggestions;

  // Group by category, preserving the canonical order regardless of how
  // the backend returned them
  const categoryOrder: Array<SuggestedQuestion['category']> = [
    'explore',
    'quotes',
    'compare',
    'report',
  ];
  const grouped = displaySuggestions.reduce(
    (acc, s) => {
      if (!acc[s.category]) acc[s.category] = [];
      acc[s.category].push(s);
      return acc;
    },
    {} as Record<string, SuggestedQuestion[]>,
  );
  const categories = categoryOrder.filter((c) => grouped[c]?.length > 0);

  return (
    <div className="flex flex-col items-center justify-center min-h-full px-6 py-10 max-w-5xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-primary shadow-sm mb-5">
        <Sparkles className="h-7 w-7 text-white" />
      </div>

      <h2 className="text-2xl font-semibold text-neutral-800 mb-2">
        {lang.get('msg.researchAssistant')}
      </h2>

      <p className="text-sm text-neutral-500 text-center mb-2 max-w-2xl leading-relaxed">
        {respondentCount
          ? lang
              .get('msg.suggestHeaderIntroWithCount')
              .replace('{count}', String(respondentCount))
          : lang.get('msg.suggestHeaderIntroNoCount')}
      </p>

      {interviewGoal && (
        <p className="text-xs text-neutral-400 text-center mb-8 max-w-xl italic">
          {lang.get('msg.suggestStudyGoalLabel')} {truncate(interviewGoal, 100)}
        </p>
      )}

      {/* Category cards */}
      <div
        // Use a 2-column grid: each card takes one column, fits on most screens
        className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full"
      >
        {categories.map((cat) => {
          const style = CATEGORY_STYLES[cat];
          const Icon = style.icon;
          const label = lang.get(CATEGORY_LABEL_KEYS[cat]);

          return (
            <div key={cat} className="flex flex-col gap-2.5">
              {/* Category header with colored icon badge */}
              <div className="flex items-center gap-2.5 mb-0.5">
                <div
                  className={cn(
                    'flex items-center justify-center h-8 w-8 rounded-lg',
                    style.iconBg,
                  )}
                >
                  <Icon className={cn('h-4 w-4', style.iconColor)} />
                </div>
                <span
                  className={cn('text-xs font-bold uppercase tracking-wider', style.labelColor)}
                >
                  {label}
                </span>
              </div>

              {/* Suggestion cards */}
              {grouped[cat].slice(0, 3).map((suggestion) => (
                <button
                  key={suggestion.text}
                  type="button"
                  onClick={() => !disabled && onSelectQuestion(suggestion.text)}
                  disabled={disabled}
                  className={cn(
                    'group/card text-left text-sm text-neutral-700 bg-white border rounded-xl px-4 py-3 transition-all leading-relaxed flex items-start justify-between gap-2',
                    style.border,
                    !disabled && style.hoverBorder,
                    !disabled && style.hoverBg,
                    !disabled && 'hover:shadow-sm cursor-pointer',
                    disabled && 'opacity-50 cursor-not-allowed',
                  )}
                  title={suggestion.text}
                >
                  <span className="flex-1">{suggestion.text}</span>
                  <ArrowRight
                    className={cn(
                      'h-3.5 w-3.5 mt-0.5 shrink-0 opacity-0 -translate-x-1 transition-all',
                      !disabled && 'group-hover/card:opacity-100 group-hover/card:translate-x-0',
                      style.iconColor,
                    )}
                  />
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* Subtle helper text at bottom */}
      <p className="text-xs text-neutral-400 text-center mt-8">
        {lang.get('msg.suggestTypeOwnQuestion')}
      </p>
    </div>
  );
}

export default SuggestedQuestions;
